import { useQuery } from '@tanstack/react-query';
import { Loader2, Tag, FileText } from 'lucide-react';

import { adminFetch } from '@/hooks/admin/useAdminApi';
import { formatUsd } from '@/lib/format';
import { useCustomerDetailContext } from '../AdminCustomerDetail';

interface CustomerPricing {
  ok: boolean;
  customer_name: string;
  qbo_customer_id: number | null;
  contract: { name: string; start_date: string; end_date: string | null } | null;
  rows: Array<{
    qbo_item_id: string;
    item_name: string;
    unit_price: number;
    source: string;
    /** Per-container pass-through taxes owed at this customer's delivery cities. */
    taxes?: Array<{ kind: string; label: string; amount: number; cities: string[] }>;
  }>;
}

/**
 * Pricing tab — shows the resolved pricing this store gets from the central
 * Pricing Control Center: its contract (if any), assigned price book, or
 * BX-1 standard, with the
 * per-item prices and where each one comes from. Read-only (edit lives in the
 * Refractor Pricing section).
 */
export default function PricingTab() {
  const { customer } = useCustomerDetailContext();
  const { data, isLoading, error } = useQuery<CustomerPricing>({
    queryKey: ['admin', 'customer-pricing', customer.id],
    queryFn: () => adminFetch<CustomerPricing>(`admin-customer-pricing?customer_id=${customer.id}`),
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="py-10 text-center text-sm text-brix-muted">
        <Loader2 size={16} className="animate-spin inline-block mr-2" />
        Loading pricing…
      </div>
    );
  }
  if (error || !data?.ok) {
    return (
      <div className="rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
        {error instanceof Error ? error.message : 'Failed to load pricing'}
      </div>
    );
  }

  const onContract = Boolean(data.contract);
  // A non-default assigned price book (e.g. BX-2 CANS) shows up as its code in
  // `source`; surface it in the banner instead of claiming BX-1 standard.
  const hasTaxes = data.rows.some((r) => (r.taxes?.length ?? 0) > 0);
  // A master account can span cities that tax differently (M FOODS: Seattle +
  // Kirkland), so a tax that doesn't reach every location names its cities.
  const allCities = new Set(data.rows.flatMap((r) => (r.taxes ?? []).flatMap((t) => t.cities)));
  const bookCodes = Array.from(
    new Set(data.rows.map((r) => r.source).filter((s) => s !== 'contract' && s !== 'BX-1')),
  );

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Tier banner */}
      <div
        className={
          'rounded-xl border px-4 py-3 flex items-start gap-3 ' +
          (onContract
            ? 'border-brix-primary/30 bg-brix-primary-tint/50'
            : 'border-brix-border bg-brix-surface-alt/40')
        }
      >
        <span className="mt-0.5 text-brix-primary">
          {onContract ? <FileText size={16} /> : <Tag size={16} />}
        </span>
        <div>
          {onContract ? (
            <>
              <p className="text-sm font-semibold text-brix-ink">
                Contract pricing — {data.contract!.name}
              </p>
              <p className="text-[11px] text-brix-muted mt-0.5">
                Effective {data.contract!.start_date}
                {data.contract!.end_date ? ` → ${data.contract!.end_date}` : ' (no end date)'} ·
                overrides the BX-1 standard for the items below.
              </p>
            </>
          ) : bookCodes.length > 0 ? (
            <>
              <p className="text-sm font-semibold text-brix-ink">
                Price book — {bookCodes.join(', ')}
              </p>
              <p className="text-[11px] text-brix-muted mt-0.5">
                Assigned price book · overrides the BX-1 standard for the items it covers.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-brix-ink">BX-1 standard pricing</p>
              <p className="text-[11px] text-brix-muted mt-0.5">
                This store is on the standard price list (no contract).
              </p>
            </>
          )}
        </div>
      </div>

      {/* Resolved prices */}
      {data.rows.length === 0 ? (
        <p className="text-sm text-brix-muted italic">
          No central pricing yet for this customer — orders fall back to catalog list price.
        </p>
      ) : (
        <div className="rounded-xl border border-brix-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brix-surface-alt/60 text-[11px] uppercase tracking-tight text-brix-muted">
              <tr>
                <th className="text-left font-semibold px-3 py-2">Item</th>
                <th className="text-right font-semibold px-3 py-2">Price</th>
                {hasTaxes && (
                  <>
                    <th className="text-left font-semibold px-3 py-2">Pass-through taxes</th>
                    <th className="text-right font-semibold px-3 py-2">Delivered</th>
                  </>
                )}
                <th className="text-left font-semibold px-3 py-2 w-28">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brix-border">
              {data.rows.map((r) => (
                <tr key={r.qbo_item_id}>
                  <td className="px-3 py-2 text-brix-ink">{r.item_name}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-brix-ink">
                    {formatUsd(r.unit_price)}
                  </td>
                  {hasTaxes && (
                    <>
                      <td className="px-3 py-2 text-[11px] text-brix-muted">
                        {(r.taxes ?? []).length === 0
                          ? '—'
                          : r.taxes!.map((t) => (
                              <div key={`${t.kind}:${t.amount}`}>
                                {t.label} {formatUsd(t.amount)}
                                {allCities.size > 1 && t.cities.length < allCities.size
                                  ? ` (${t.cities.join(', ')})`
                                  : ''}
                              </div>
                            ))}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-brix-ink">
                        {(r.taxes ?? []).length === 0
                          ? formatUsd(r.unit_price)
                          : formatUsd(
                              Number(r.unit_price) + r.taxes!.reduce((n, t) => n + Number(t.amount), 0),
                            )}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2">
                    <span
                      className={
                        'inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ' +
                        (r.source === 'contract'
                          ? 'bg-brix-primary text-white'
                          : 'bg-brix-surface-alt text-brix-muted')
                      }
                    >
                      {r.source === 'contract' ? 'Contract' : r.source}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
